import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import Amplify from "aws-amplify";
import featureFlags from "./modules/featureFlags";
import notifications from "./modules/notifications";
import cancellations from "./modules/cancellations";
import messages from "./modules/messages";

Vue.use(Vuex);

var logger = new Amplify.Logger("Store");
// OPT-310: refreshAccounts calls; a superseded one does not commit.
let accountsGeneration = 0;

function _initialState() {
  return {
    user: null,
    isNavExpanded: true,
    isRightDrawerOpen: false,
    nav: { title: "", icon: "", path: "", color: "", rightDrawerForm: "" },
    rightDrawerData: {},
    tasks: [],
    accounts: [],
    // The seller's last choice for "remove the background from the first 2 photos" (default on; free).
    imageAiOptInV2: true,
  };
}

export default new Vuex.Store({
  plugins: [
    createPersistedState({
      paths: [
        "user",
        "isNavExpanded",
        "isRightDrawerOpen",
        "nav",
        "rightDrawerData",
        "tasks",
        "accounts",
        "imageAiOptInV2",
        "featureFlags",
      ],
    }),
  ],
  state: _initialState(),
  mutations: {
    SET_IMAGE_AI_OPT_IN(state, value) {
      state.imageAiOptInV2 = Boolean(value);
    },
    SET_ACCOUNTS(state, accounts) {
      state.accounts = accounts;
    },
    SET_USER(state, user) {
      if (null != user) {
        // Admins = operator only (docs/access-table.md); customers are Subscribers.
        const groups = user.signInUserSession.idToken.payload["cognito:groups"] || [];

        state.user = {
          email: user.attributes.email,
          username: user.attributes.sub,
          isAdmin: groups.includes("Admins"),
          isSubscriber: groups.includes("Subscribers"),
        };
      } else {
        user = null;
      }
    },
    RESET: (state) => {
      accountsGeneration++;
      const s = _initialState();
      Object.keys(s).forEach((key) => {
        state[key] = s[key];
      });
    },
    TOGGLE_NAV: (state) => {
      state.isNavExpanded = !state.isNavExpanded;
    },
    TOGGLE_DRAWER: (state) => {
      state.isRightDrawerOpen = !state.isRightDrawerOpen;
    },
    SET_DRAWER_DATA: (state, data) => {
      state.rightDrawerData = data;
    },
  },
  getters: {
    hasAccounts: (state) => {
      logger.info("hasAccountsGetter()", state.accounts.length);
      return state.accounts.length > 0 ? true : false;
    },
    isAdmin: (state) => state.user !== null && state.user.isAdmin === true,
    isSubscriber: (state) => state.user !== null && state.user.isSubscriber === true,
    // OPT-203: a customer (Subscribers) or the operator (Admins) may use the app.
    hasAppAccess: (state, getters) => getters.isAdmin || getters.isSubscriber,
  },
  actions: {
    // OPT-310: the owner's accounts, every page. A failed or partial read
    // throws and leaves the last good list in place (never an empty one).
    // Only the latest call commits, and only for the user it started for, so
    // a slow read cannot overwrite a newer list or land after sign-out.
    async refreshAccounts({ commit, state }) {
      const { fetchAllAccounts } = await import("@/utils/allAccounts");
      const generation = ++accountsGeneration;
      const who = state.user && state.user.username;
      const items = await fetchAllAccounts();
      if (generation !== accountsGeneration || !who || !state.user || state.user.username !== who) return null;
      commit("SET_ACCOUNTS", items);
      return items;
    },
    async initializeApp({ dispatch }) {
      // Initialize feature flags when app starts
      await dispatch('featureFlags/initializeFlags');
    }
  },
  modules: {
    featureFlags,
    notifications,
    cancellations,
    messages
  },
});
